import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Calendar, Euro, Users, AlertCircle,
  FolderOpen, Clock, CreditCard, ChevronRight,
  FileText, Camera, Loader2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, ReferenceLine,
} from 'recharts';
import { apiGet } from '../api/client';
import { useBlobDownload } from '../hooks/useBlobDownload';
import type { DashboardStats } from '../api/types';
import { EventStatusBadge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { PageHeader } from '../components/ui/PageHeader';
import { UserAvatar } from '../components/ui/UserAvatar';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermission';
import { formatCurrency, formatPercent, formatDate, formatEventType, clientFullName } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const EVENT_STATUS_LABELS: Record<string, string> = {
  PREVENTIVO: 'Preventivo', CONTRATTO: 'Contratto', CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione', CONSEGNATO: 'Consegnato', ARCHIVIATO: 'Archiviato',
};
const EVENT_STATUS_COLORS: Record<string, string> = {
  PREVENTIVO: 'var(--chart-violet)', CONTRATTO: 'var(--chart-indigo)', CONFERMATO: 'var(--chart-blue)',
  IN_LAVORAZIONE: 'var(--chart-azure)', CONSEGNATO: 'var(--chart-violet)', ARCHIVIATO: 'var(--chart-indigo)',
};
const PRIORITY_COLORS: Record<string, string> = {
  URGENTE: 'var(--color-error)', ALTA: 'var(--color-warning)',
  MEDIA: 'var(--color-primary)', BASSA: 'var(--color-text-faint)',
};
const PROJECT_STATUS_LABELS: Record<string, string> = {
  BOZZA: 'Bozza', PREVENTIVATO: 'Preventivato', CONFERMATO: 'Confermato',
  IN_CORSO: 'In corso', IN_ATTESA: 'In attesa', COMPLETATO: 'Completato',
  FATTURATO: 'Fatturato', CHIUSO: 'Chiuso', ANNULLATO: 'Annullato',
};

// ─── Greeting ─────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Buongiorno';
  if (h < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ loading, label, value, trend, icon, color, subtitle, href }: {
  loading: boolean; label: string; value: string;
  trend?: number; icon: React.ReactNode; color: string; subtitle?: string; href?: string;
}) {
  const navigate = useNavigate();
  const clickable = !!href;
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => navigate(href!) : undefined}
      onKeyDown={clickable ? e => e.key === 'Enter' && navigate(href!) : undefined}
      className={clickable ? 'hover-lift' : undefined}
      style={{
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '1.25rem', display: 'flex',
        flexDirection: 'column', gap: '0.5rem',
        cursor: clickable ? 'pointer' : 'default',
        transition: `border-color var(--transition-interactive), box-shadow var(--transition-interactive), transform var(--duration-fast) var(--ease-out)`,
      }}
      onMouseEnter={clickable ? e => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-primary)';
      } : undefined}
      onMouseLeave={clickable ? e => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255,255,255,0.10)';
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
          fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{label}</span>
        <span style={{ color, opacity: 0.75 }}>{icon}</span>
      </div>
      {loading ? <Skeleton height="1.75rem" width="60%" /> : (
        <>
          <span style={{
            fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--color-text)',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{value}</span>
          {trend !== undefined && (
            <span style={{
              fontSize: 'var(--text-xs)', fontWeight: 500,
              color: trend >= 0 ? 'var(--color-success)' : 'var(--color-error)',
              display: 'flex', alignItems: 'center', gap: '0.125rem',
            }}>
              {trend >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              {formatPercent(trend)}
            </span>
          )}
          {subtitle && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>
          )}
        </>
      )}
    </div>
  );
}

function SectionCard({ title, icon, children, action }: {
  title: string; icon?: React.ReactNode; children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div style={{
      background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)', padding: '1.25rem', display: 'flex',
      flexDirection: 'column', gap: '1rem',
      minWidth: 0, boxSizing: 'border-box', width: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0, gap: '0.5rem' }}>
        <h3 className="dash-section-title" style={{
          fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)',
          display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0,
        }}>
          {icon}{title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text, cta, onCta }: { text: string; cta?: string; onCta?: () => void }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '1.5rem 0', gap: '0.5rem',
    }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', textAlign: 'center' }}>{text}</p>
      {cta && onCta && (
        <button onClick={onCta} style={{
          fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 500,
          background: 'none', border: 'none', cursor: 'pointer', display: 'flex',
          alignItems: 'center', gap: '0.25rem',
        }}>
          {cta} <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

function RowButton({ onClick, left, right }: {
  onClick: () => void;
  left: React.ReactNode;
  right: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.5rem 0.625rem', background: 'var(--glass-bg-card)',
        border: '1px solid transparent', borderRadius: 'var(--radius-card)',
        cursor: 'pointer', textAlign: 'left', width: '100%', minWidth: 0,
        overflow: 'hidden', gap: '0.5rem',
        transition: 'border-color var(--transition-interactive)',
      }}
      onMouseEnter={e => { (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'); }}
      onMouseLeave={e => { (e.currentTarget.style.borderColor = 'transparent'); }}
    >
      {left}
      {right}
    </button>
  );
}

// ─── Action Feed ──────────────────────────────────────────────────────────────

interface ActionItem {
  id: string;
  type: 'deadline' | 'editor_payment' | 'aggregate';
  label: string;
  sub: string;
  amount?: number;
  urgency: 'high' | 'medium' | 'low';
  href?: string;
  avatar?: { url?: string | null; name: string };
}

function urgencyDot(urgency: ActionItem['urgency']) {
  const colors = { high: 'var(--color-error)', medium: 'var(--color-warning)', low: 'var(--color-text-faint)' };
  return (
    <span style={{
      width: 8, height: 8, borderRadius: '50%', background: colors[urgency],
      flexShrink: 0, display: 'inline-block',
    }} />
  );
}

// ─── Charts ───────────────────────────────────────────────────────────────────

function RevenueChart({ data, loading }: { data?: { month: string; revenue: number }[]; loading: boolean }) {
  const avg = useMemo(() => {
    if (!data?.length) return 0;
    const nonZero = data.filter(d => d.revenue > 0);
    if (!nonZero.length) return 0;
    return nonZero.reduce((s, d) => s + d.revenue, 0) / nonZero.length;
  }, [data]);

  if (loading) return <Skeleton height={180} />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data ?? []} barSize={22} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <XAxis dataKey="month" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
          axisLine={false} tickLine={false}
          tickFormatter={v => v >= 1000 ? `€${(v / 1000).toFixed(0)}k` : `€${v}`}
          width={44}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: '12px',
          }}
          formatter={(v: number) => [formatCurrency(v), 'Entrate']}
        />
        {avg > 0 && (
          <ReferenceLine
            y={avg} stroke="var(--color-text-faint)" strokeDasharray="4 3"
            label={{ value: 'media', position: 'insideTopRight', fill: 'var(--color-text-faint)', fontSize: 10 }}
          />
        )}
        <Bar dataKey="revenue" fill="var(--chart-violet)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StatusDonut({ data: eventsByStatus, loading }: { data?: Record<string, number>; loading: boolean }) {
  const statusData = Object.entries(eventsByStatus ?? {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: EVENT_STATUS_LABELS[k] ?? k, value: v, key: k }));
  const total = statusData.reduce((s, d) => s + d.value, 0);

  if (loading) return <Skeleton height={180} />;
  if (!statusData.length) return <EmptyState text="Nessun evento" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <ResponsiveContainer width="100%" height={130}>
        <PieChart>
          <Pie data={statusData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={2}>
            {statusData.map(d => <Cell key={d.key} fill={EVENT_STATUS_COLORS[d.key] ?? '#6b7280'} />)}
          </Pie>
          <Tooltip
            contentStyle={{
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)', fontSize: '12px',
            }}
            formatter={(v: number, name: string) => [`${v} (${Math.round(v / total * 100)}%)`, name]}
          />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {statusData.map(d => (
          <div key={d.key} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: 'var(--text-xs)',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-text-muted)' }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: EVENT_STATUS_COLORS[d.key], flexShrink: 0, display: 'inline-block',
              }} />
              {d.name}
            </span>
            <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const { hasPermission } = usePermissions();
  const canKpi = hasPermission('dashboard.kpi');
  const canCharts = hasPermission('dashboard.charts');
  const canFeed = hasPermission('dashboard.feed');
  const canActivity = hasPermission('dashboard.activity');
  const canReport = hasPermission('dashboard.report');
  const { data, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiGet('/dashboard/stats'),
    refetchInterval: 60_000,
  });
  const today = new Date();
  const [reportMonth, setReportMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  );
  const { isDownloading: isExportingReport, downloadBlob } = useBlobDownload();

  // Build unified action feed
  const actionItems = useMemo((): ActionItem[] => {
    const items: ActionItem[] = [];

    // Scadenze progetto (next 7 days) — alta urgenza
    (data?.upcomingDeadlines ?? []).forEach(p => {
      const daysLeft = p.deadline
        ? Math.ceil((new Date(p.deadline).getTime() - Date.now()) / 86400000)
        : null;
      items.push({
        id: `deadline-${p.id}`,
        type: 'deadline',
        label: p.title,
        sub: `Scadenza ${p.deadline ? formatDate(p.deadline) : '—'} · ${p.priority}`,
        urgency: daysLeft !== null && daysLeft <= 1 ? 'high' : 'medium',
        href: `/projects/${p.id}`,
      });
    });

    // Pagamenti editor singoli
    (data?.pendingEditorPayments ?? []).forEach(p => {
      items.push({
        id: `editor-${p.id}`,
        type: 'editor_payment',
        label: p.collaborator.name,
        sub: p.project.title,
        amount: p.feeExpected - p.feePaid,
        urgency: 'medium',
        href: `/projects/${p.project.id}`,
        avatar: { url: p.collaborator.avatarUrl, name: p.collaborator.name },
      });
    });

    // KPI aggregati come action items (solo se > 0)
    if ((data?.kpi.pendingDeposits ?? 0) > 0) {
      items.push({
        id: 'agg-deposits',
        type: 'aggregate',
        label: 'Acconti da incassare',
        sub: `${formatCurrency(data!.kpi.pendingDeposits)} in sospeso`,
        urgency: 'medium',
        href: '/events',
      });
    }
    if ((data?.kpi.pendingEventExpenses ?? 0) > 0) {
      items.push({
        id: 'agg-expenses',
        type: 'aggregate',
        label: 'Spese evento da saldare',
        sub: formatCurrency(data!.kpi.pendingEventExpenses),
        urgency: 'low',
        href: '/events',
      });
    }
    if ((data?.kpi.unpaidCollaborators ?? 0) > 0) {
      items.push({
        id: 'agg-collab',
        type: 'aggregate',
        label: 'Collaboratori evento da pagare',
        sub: formatCurrency(data!.kpi.unpaidCollaborators),
        urgency: 'low',
        href: '/events',
      });
    }
    if ((data?.kpi.unpaidExternalSales ?? 0) > 0) {
      items.push({
        id: 'agg-external',
        type: 'aggregate',
        label: 'Da incassare (esterni)',
        sub: formatCurrency(data!.kpi.unpaidExternalSales),
        urgency: 'low',
        href: '/projects',
      });
    }

    // Sort: high first
    return items.sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[a.urgency] - order[b.urgency];
    });
  }, [data]);

  const totalActions = actionItems.length;

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto', boxSizing: 'border-box' }}>
      {/* style inline spostato in head via global.css per debug #300 */}

      <PageHeader
        title={`${greeting()}${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        subtitle={`${today.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}${(data?.kpi.upcomingCount ?? 0) > 0 ? ` · ${data!.kpi.upcomingCount} event${data!.kpi.upcomingCount === 1 ? 'o' : 'i'} nei prossimi 30 giorni` : ''}`}
      >
        {canReport && (
          <div className="dash-report-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="month"
              value={reportMonth}
              onChange={e => setReportMonth(e.target.value)}
              style={{
                background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
                fontSize: 'var(--text-sm)', padding: '0.4rem 0.75rem', outline: 'none',
              }}
            />
            <button
              onClick={() => downloadBlob(`/reports/monthly/pdf?month=${reportMonth}`, `report-${reportMonth}.pdf`, 'Report mensile')}
              disabled={isExportingReport}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                background: 'var(--color-primary)', color: '#fff', border: 'none',
                borderRadius: 'var(--radius-card)', padding: '0.45rem 1rem',
                fontSize: 'var(--text-sm)', fontWeight: 600,
                cursor: isExportingReport ? 'not-allowed' : 'pointer',
                opacity: isExportingReport ? 0.7 : 1,
              }}
            >
              {isExportingReport ? <Loader2 size={14} className="spin" /> : <FileText size={14} />}
              {isExportingReport ? 'Generazione...' : 'Report mensile'}
            </button>
          </div>
        )}
      </PageHeader>

      {/* KPI Row — 5 card primarie */}
      {canKpi && <div className="stagger-children grid-fluid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
        <KpiCard
          loading={isLoading}
          label="Entrate mese corrente"
          value={formatCurrency(data?.kpi.currentMonthRevenue)}
          trend={data?.kpi.revenueTrend}
          icon={<Euro size={18} />}
          color="var(--color-primary)"
          subtitle={data?.kpi.prevMonthRevenue ? `vs ${formatCurrency(data.kpi.prevMonthRevenue)} mese scorso` : undefined}
          href="/events"
        />
        <KpiCard
          loading={isLoading}
          label="Anno in corso (YTD)"
          value={formatCurrency(data?.kpi.revenueYTD)}
          icon={<TrendingUp size={18} />}
          color="var(--color-success)"
          href="/events"
        />
        <KpiCard
          loading={isLoading}
          label="Acconti in attesa"
          value={formatCurrency(data?.kpi.pendingDeposits)}
          icon={<AlertCircle size={18} />}
          color="var(--color-warning)"
          href="/events"
        />
        <KpiCard
          loading={isLoading}
          label="Prossimi eventi (30gg)"
          value={String(data?.kpi.upcomingCount ?? 0)}
          icon={<Calendar size={18} />}
          color="var(--color-success)"
          href="/events"
        />
        <KpiCard
          loading={isLoading}
          label="Progetti attivi"
          value={String(data?.kpi.activeProjects ?? 0)}
          icon={<FolderOpen size={18} />}
          color="var(--color-primary)"
          subtitle={data?.kpi.externalJobsInProgress ? `${data.kpi.externalJobsInProgress} ext. in corso` : undefined}
          href="/projects"
        />
      </div>}

      {/* Charts Row */}
      {canCharts && (
        <div className="dash-charts-row">
          <SectionCard title="Entrate ultimi 6 mesi">
            <RevenueChart data={data?.monthlyRevenue} loading={isLoading} />
          </SectionCard>
          <SectionCard title="Distribuzione eventi">
            <StatusDonut data={data?.eventsByStatus} loading={isLoading} />
          </SectionCard>
        </div>
      )}

      {/* Actions + Upcoming Events */}
      {canFeed && <div className="dash-actions-row">

        {/* Azioni richieste */}
        <SectionCard
          title={`Da fare${totalActions > 0 ? ` (${totalActions})` : ''}`}
          icon={<AlertCircle size={15} style={{ color: totalActions > 0 ? 'var(--color-warning)' : 'var(--color-text-faint)' }} />}
        >
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[1, 2, 3].map(i => <Skeleton key={i} height={48} />)}
            </div>
          ) : totalActions === 0 ? (
            <EmptyState text="Tutto in ordine — nessuna azione richiesta" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: 280, overflowY: 'auto' }}>
              {actionItems.map(item => (
                <RowButton
                  key={item.id}
                  onClick={() => item.href && navigate(item.href)}
                  left={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', minWidth: 0 }}>
                      {urgencyDot(item.urgency)}
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {item.avatar && (
                          <UserAvatar avatarUrl={item.avatar.url} name={item.avatar.name} size={20} />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <p style={{
                          fontSize: 'var(--text-xs)', fontWeight: 600,
                          color: 'var(--color-text)', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{item.label}</p>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sub}</p>
                      </div>
                    </div>
                  </div>
                  }
                  right={
                    item.amount !== undefined
                      ? <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-gold)', flexShrink: 0, marginLeft: '0.5rem' }}>
                          {formatCurrency(item.amount)}
                        </span>
                      : <ChevronRight size={14} style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
                  }
                />
              ))}
            </div>
          )}
        </SectionCard>

        {/* Prossimi eventi */}
        <SectionCard
          title="Prossimi eventi"
          icon={<Calendar size={15} style={{ color: 'var(--color-primary)' }} />}
          action={
            <button
              onClick={() => navigate('/events')}
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}
            >
              Tutti <ChevronRight size={12} />
            </button>
          }
        >
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[1, 2, 3].map(i => <Skeleton key={i} height={48} />)}
            </div>
          ) : !data?.upcomingEvents.length ? (
            <EmptyState text="Nessun evento nei prossimi 30 giorni" cta="Crea evento" onCta={() => navigate('/events')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {data.upcomingEvents.map(ev => (
                <RowButton
                  key={ev.id}
                  onClick={() => navigate(`/events/${ev.id}`)}
                  left={
                    <div className="text-safe" style={{ minWidth: 0 }}>
                      <p className="text-safe" style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</p>
                      <p className="text-safe" style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {clientFullName(ev.client)} · {formatDate(ev.eventDate)}
                      </p>
                    </div>
                  }
                  right={<EventStatusBadge status={ev.status} />}
                />
              ))}
            </div>
          )}
        </SectionCard>
      </div>}

      {/* Activity + External jobs */}
      {canActivity && <div className="dash-activity-row">

        {/* Attività recente */}
        <SectionCard title="Attività recente">
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[1, 2, 3].map(i => <Skeleton key={i} height={40} />)}
            </div>
          ) : !(data?.recentEvents ?? []).length ? (
            <EmptyState text="Nessun evento recente" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {(data?.recentEvents ?? []).map((ev, idx) => (
                <button
                  key={ev.id}
                  onClick={() => navigate(`/events/${ev.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.625rem 0', background: 'none', border: 'none',
                    borderBottom: idx < (data?.recentEvents.length ?? 0) - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                    cursor: 'pointer', textAlign: 'left', width: '100%', minWidth: 0,
                    overflow: 'hidden', gap: '0.5rem',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0 }}>
                    <span style={{
                      fontSize: 'var(--text-xs)', color: 'var(--color-text)', fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{ev.title}</span>
                    <span style={{
                      fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {formatEventType(ev.eventType)} · {clientFullName(ev.client)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginLeft: '0.5rem', minWidth: 0 }}>
                    <EventStatusBadge status={ev.status} />
                    <span className="dash-event-date" style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                      {formatDate(ev.eventDate)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Post-produzione esterna */}
        <SectionCard
          title="Post-produzione esterna"
          icon={<Camera size={15} style={{ color: 'var(--color-primary)' }} />}
          action={
            data?.kpi.unpaidExternalSales
              ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)', fontWeight: 600 }}>
                  {formatCurrency(data.kpi.unpaidExternalSales)} da incassare
                </span>
              : undefined
          }
        >
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[1, 2].map(i => <Skeleton key={i} height={48} />)}
            </div>
          ) : !data?.recentExternalJobs?.length ? (
            <EmptyState text="Nessun lavoro esterno recente" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {data.recentExternalJobs.map(j => (
                <RowButton
                  key={j.id}
                  onClick={() => navigate(`/projects/${j.id}`)}
                  left={
                    <div style={{ minWidth: 0 }}>
                      <p style={{
                        fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{j.title}</p>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {j.externalPhotographer
                          ? (j.externalPhotographer.studioName ?? `${j.externalPhotographer.firstName} ${j.externalPhotographer.lastName}`)
                          : '—'}
                        {j.photosToEditCount ? ` · ${j.photosDeliveredCount ?? 0}/${j.photosToEditCount} foto` : ''}
                      </p>
                    </div>
                  }
                  right={
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '0.5rem' }}>
                      <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text)' }}>
                        {formatCurrency(j.externalSalePrice)}
                      </p>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                        {PROJECT_STATUS_LABELS[j.status] ?? j.status}
                      </p>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </SectionCard>
      </div>}
    </div>
  );
}
